import { EventDelegate } from '../utils/EventDelegate';
import { Vector2 } from '@math.gl/core';
export var MouseButtons;
(function (MouseButtons) {
    MouseButtons[MouseButtons["None"] = 0] = "None";
    MouseButtons[MouseButtons["Left"] = 1] = "Left";
    MouseButtons[MouseButtons["Right"] = 2] = "Right";
    MouseButtons[MouseButtons["Middle"] = 4] = "Middle";
})(MouseButtons || (MouseButtons = {}));
/**
 * 汎用的な統合入力ハンドリング
 */
export class InputHandler {
    onKeyDown = new EventDelegate();
    onKeyUp = new EventDelegate();
    onPointerDown = new EventDelegate();
    onPointerMove = new EventDelegate();
    onPointerLocalMove = new EventDelegate();
    onPointerUp = new EventDelegate();
    onMouseDown = new EventDelegate();
    onMouseMove = new EventDelegate();
    onMouseLocalMove = new EventDelegate();
    onMouseUp = new EventDelegate();
    onTouchStart = new EventDelegate();
    onTouchMove = new EventDelegate();
    onTouchLocalMove = new EventDelegate();
    onTouchEnd = new EventDelegate();
    onInputStart = new EventDelegate();
    onInputMove = new EventDelegate();
    onInputLocalMove = new EventDelegate();
    onInputEnd = new EventDelegate();
    onWheel = new EventDelegate();
    onClick = new EventDelegate();
    onDelayedClick = new EventDelegate();
    onDblClick = new EventDelegate();
    _events = {};
    isTouchDown = false;
    isMouseDown = false;
    isPointerDown = false;
    isMoved = false;
    isPrimary = false;
    isDown = false;
    startPosition = { x: 0, y: 0 };
    lastClickTime = 0;
    clickTimer = 0;
    constructor(el) {
        this._events['key-down'] = this.keyDown.bind(this);
        this._events['key-up'] = this.keyUp.bind(this);
        this._events['mouse-down'] = this.mouseDown.bind(this);
        this._events['mouse-move'] = this.mouseMove.bind(this);
        this._events['mouse-local-move'] = this.mouseLocalMove.bind(this);
        this._events['mouse-up'] = this.mouseUp.bind(this);
        this._events['pointer-down'] = this.pointerDown.bind(this);
        this._events['pointer-move'] = this.pointerMove.bind(this);
        this._events['pointer-local-move'] = this.pointerLocalMove.bind(this);
        this._events['pointer-up'] = this.pointerUp.bind(this);
        this._events['touch-start'] = this.touchStart.bind(this);
        this._events['touch-move'] = this.touchMove.bind(this);
        this._events['touch-local-move'] = this.touchLocalMove.bind(this);
        this._events['touch-end'] = this.touchEnd.bind(this);
        this._events['wheel'] = this.wheel.bind(this);
        this._events['prevent'] = this.prevent.bind(this);
        if (el) {
            this.el = el;
        }
    }
    _el;
    get el() {
        return this._el;
    }
    set el(el) {
        if (this._el) {
            this.removeHandlers();
        }
        this._el = el;
        if (this._el) {
            this.addHandlers();
        }
    }
    _eventPosition = new Vector2();
    get eventPosition() {
        return this._eventPosition;
    }
    _mouseScrollDelta = new Vector2();
    get mouseScrollDelta() {
        return this._mouseScrollDelta;
    }
    dispose() {
        this.removeHandlers();
    }
    onEndOfFrame() {
        this._mouseScrollDelta.set(0, 0);
    }
    removeHandlers() {
        const handler = this._el;
        if (!handler) {
            return;
        }
        // handler.removeEventListener('keydown', this._events['keydown'], true);
        // window.removeEventListener('keyup', this._events['keyup'], true);
        if ('onpointerdown' in window) {
            handler.removeEventListener('pointerdown', this._events['pointer-down'], true);
            handler.removeEventListener('pointermove', this._events['pointer-local-move'], true);
            window.removeEventListener('pointermove', this._events['pointer-move'], true);
            window.removeEventListener('pointerup', this._events['pointer-up'], true);
        }
        else {
            handler.removeEventListener('mousedown', this._events['mouse-down'], true);
            handler.removeEventListener('mousemove', this._events['mouse-local-move'], true);
            window.removeEventListener('mousemove', this._events['mouse-move'], true);
            window.removeEventListener('mouseup', this._events['mouse-up'], true);
        }
        handler.removeEventListener('contextmenu', this._events['prevent']);
        handler.removeEventListener('touchstart', this._events['touch-start'], true);
        window.removeEventListener('touchmove', this._events['touch-move'], true);
        handler.removeEventListener('touchmove', this._events['touch-local-move'], true);
        window.removeEventListener('touchend', this._events['touch-end'], true);
        handler.removeEventListener('wheel', this._events['wheel']);
    }
    addHandlers() {
        const handler = this._el;
        if (!handler) {
            return;
        }
        // handler.addEventListener('keydown', this._events['key-down']);
        // window.addEventListener('keyup', this._events['key-up']);
        if ('onpointerdown' in window) {
            handler.addEventListener('pointerdown', this._events['pointer-down'], { passive: false });
            handler.addEventListener('pointermove', this._events['pointer-local-move'], { passive: false });
            window.addEventListener('pointermove', this._events['pointer-move'], { passive: false });
            window.addEventListener('pointerup', this._events['pointer-up'], { passive: false });
        }
        else {
            handler.addEventListener('mousedown', this._events['mouse-down'], { passive: false });
            handler.addEventListener('mousemove', this._events['mouse-local-move'], { passive: false });
            window.addEventListener('mousemove', this._events['mouse-move'], { passive: false });
            window.addEventListener('mouseup', this._events['mouse-up'], { passive: false });
        }
        handler.addEventListener('contextmenu', this._events['prevent']);
        handler.addEventListener('touchstart', this._events['touch-start'], { passive: false });
        window.addEventListener('touchmove', this._events['touch-move'], { passive: false });
        handler.addEventListener('touchmove', this._events['touch-local-move'], { passive: false });
        window.addEventListener('touchend', this._events['touch-end'], { passive: false });
        handler.addEventListener('wheel', this._events['wheel']);
    }
    prevent(e) {
        e.preventDefault();
    }
    keyDown(e) {
        this.onKeyDown.emit(e);
    }
    keyUp(e) {
        this.onKeyUp.emit(e);
    }
    mouseDown(e) {
        this.isMouseDown = true;
        this.onMouseDown.emit(e);
        const inputs = this.toInputItems(e);
        this.inputStart({
            isTouch: false,
            isDown: this.isMouseDown,
            buttons: e.buttons,
            main: inputs[0],
            inputs,
            originalEvent: e
        });
        e.preventDefault();
    }
    mouseLocalMove(e) {
        this.onMouseLocalMove.emit(e);
        const inputs = this.toInputItems(e);
        this.inputLocalMove({
            isTouch: false,
            isDown: this.isMouseDown,
            buttons: e.buttons,
            main: inputs[0],
            inputs,
            originalEvent: e
        });
    }
    mouseMove(e) {
        this.onMouseMove.emit(e);
        const inputs = this.toInputItems(e);
        this.inputMove({
            isTouch: false,
            isDown: this.isMouseDown,
            buttons: e.buttons,
            main: inputs[0],
            inputs,
            originalEvent: e
        });
    }
    mouseUp(e) {
        this.onMouseUp.emit(e);
        const inputs = this.toInputItems(e);
        this.inputEnd({
            isTouch: false,
            isDown: this.isMouseDown,
            buttons: e.buttons,
            main: inputs[0],
            inputs,
            originalEvent: e
        });
        this.isMouseDown = false;
    }
    pointerDown(e) {
        if (!e.isPrimary || e.type === 'touch') {
            return;
        }
        this.isPointerDown = true;
        this.onPointerDown.emit(e);
        const inputs = this.toInputItems(e);
        this.inputStart({
            isTouch: false,
            isDown: this.isPointerDown,
            buttons: e.buttons,
            main: inputs[0],
            inputs,
            originalEvent: e
        });
        e.preventDefault();
    }
    pointerLocalMove(e) {
        if (e.pointerType === 'touch') {
            return;
        }
        this.onPointerLocalMove.emit(e);
        const inputs = this.toInputItems(e);
        this.inputLocalMove({
            isTouch: false,
            isDown: this.isPointerDown,
            buttons: e.buttons,
            main: inputs[0],
            inputs,
            originalEvent: e
        });
    }
    pointerMove(e) {
        if (e.pointerType === 'touch') {
            return;
        }
        this.onPointerMove.emit(e);
        const inputs = this.toInputItems(e);
        this.inputMove({
            isTouch: false,
            isDown: this.isPointerDown,
            buttons: e.buttons,
            main: inputs[0],
            inputs,
            originalEvent: e
        });
    }
    pointerUp(e) {
        if (e.pointerType === 'touch') {
            return;
        }
        this.onPointerUp.emit(e);
        const inputs = this.toInputItems(e);
        this.inputEnd({
            isTouch: false,
            isDown: this.isPointerDown,
            buttons: e.buttons,
            main: inputs[0],
            inputs,
            originalEvent: e
        });
        this.isPointerDown = false;
    }
    touchStart(e) {
        e.preventDefault();
        this.isTouchDown = true;
        this.onTouchStart.emit(e);
        const inputs = this.toInputItems(e);
        this.inputStart({
            isTouch: true,
            isDown: this.isTouchDown,
            buttons: 0,
            main: this.getMainInput(inputs),
            inputs,
            originalEvent: e
        });
    }
    touchMove(e) {
        this.onTouchMove.emit(e);
        const inputs = this.toInputItems(e);
        this.inputMove({
            isTouch: true,
            isDown: this.isTouchDown,
            buttons: 0,
            main: this.getMainInput(inputs),
            inputs,
            originalEvent: e
        });
    }
    touchLocalMove(e) {
        e.preventDefault();
        this.onTouchLocalMove.emit(e);
        const inputs = this.toInputItems(e);
        this.inputLocalMove({
            isTouch: true,
            isDown: this.isTouchDown,
            buttons: 0,
            main: this.getMainInput(inputs),
            inputs,
            originalEvent: e
        });
    }
    touchEnd(e) {
        this.onTouchEnd.emit(e);
        const inputs = [{ x: this.eventPosition.x, y: this.eventPosition.y }];
        this.inputEnd({
            isTouch: true,
            isDown: this.isTouchDown,
            buttons: 0,
            main: inputs[0],
            inputs,
            originalEvent: e
        });
        this.isTouchDown = false;
    }
    /**
     * Input系: 共通イベント
     */
    inputStart(e) {
        this.startPosition.x = e.main.x;
        this.startPosition.y = e.main.y;
        this.isMoved = false;
        this.isDown = true;
        if (e.isTouch) {
            this.isPrimary = e.inputs.length === 1;
        }
        else {
            this.isPrimary = !!(e.buttons & MouseButtons.Left);
        }
        this._eventPosition.set(e.main.x, e.main.y);
        this.onInputStart.emit(e);
    }
    inputLocalMove(e) {
        this.onInputLocalMove.emit(e);
    }
    inputMove(e) {
        const dx = this.startPosition.x - e.main.x;
        const dy = this.startPosition.y - e.main.y;
        const d2 = dx * dx + dy * dy;
        if (d2 > 15 * 15) {
            this.isMoved = true;
        }
        this._eventPosition.set(e.main.x, e.main.y);
        this.onInputMove.emit(e);
    }
    inputEnd(e) {
        this.onInputEnd.emit(e);
        if (!this.isDown) {
            return;
        }
        this.isDown = false;
        if (!this.isMoved && this.isPrimary) {
            const clickDelay = 250;
            const dt = Date.now() - this.lastClickTime;
            if (dt < clickDelay) {
                window.clearTimeout(this.clickTimer);
                this.onDblClick.emit(e);
                this.lastClickTime = 0;
            }
            else {
                this.clickTimer = window.setTimeout(() => {
                    this.onDelayedClick.emit(e);
                    this.clickTimer = 0;
                }, clickDelay);
                this.onClick.emit(e);
                this.lastClickTime = Date.now();
            }
        }
    }
    wheel(e) {
        e.preventDefault();
        this._mouseScrollDelta.set(e.deltaX, e.deltaY);
        this.onWheel.emit(e);
    }
    toInputItems(e) {
        if (e instanceof MouseEvent || (window.PointerEvent && e instanceof PointerEvent)) {
            return [{
                    x: e.pageX,
                    y: e.pageY,
                }];
        }
        else {
            return Array.prototype.map.call(e.touches, (touch) => {
                return {
                    x: touch.pageX,
                    y: touch.pageY // + window.scrollY,
                };
            });
        }
    }
    getMainInput(inputs) {
        const main = { x: 0, y: 0 };
        if (inputs.length > 0) {
            for (let i = 0; i < inputs.length; i++) {
                main.x += inputs[i].x;
                main.y += inputs[i].y;
            }
            main.x /= inputs.length;
            main.y /= inputs.length;
        }
        return main;
    }
}
//# sourceMappingURL=InputHandler.js.map